import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { createUser, getUser, updateUser } from '../api/users'
import { useAuth } from '../context/AuthContext'
import styles from './UserForm.module.css'

export default function UserForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const { token, user: currentUser, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [fetching, setFetching] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { role: 'guest' } })

  const password = watch('password')

  useEffect(() => {
    if (!authLoading && (!currentUser || currentUser.role !== 'admin')) {
      navigate('/login', { replace: true })
    }
  }, [currentUser, authLoading, navigate])

  useEffect(() => {
    if (!isEdit || !token) return
    getUser(token, id)
      .then((u) => reset({ name: u.name, username: u.username, role: u.role }))
      .catch(() => setError('User not found.'))
      .finally(() => setFetching(false))
  }, [id, token, isEdit, reset])

  const onSubmit = async (data) => {
    setSaving(true)
    setError(null)
    try {
      if (isEdit) {
        await updateUser(token, id, {
          name: data.name,
          username: data.username,
          role: data.role,
          ...(data.password ? { password: data.password } : {}),
        })
      } else {
        await createUser(token, {
          name: data.name,
          username: data.username,
          password: data.password,
          role: data.role,
          source: 'build-with-mak-page',
        })
      }
      navigate('/users')
    } catch (err) {
      const msgs = err.response?.data?.errors
      setError(msgs ? msgs.join(', ') : `Failed to ${isEdit ? 'save' : 'create'} user.`)
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || !currentUser) return null
  if (fetching) return <div style={{ padding: '48px 24px', color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <main className={styles.page}>
      <Link to="/users" className={styles.back}>
        <ArrowLeft size={16} />
        Back to Users
      </Link>
      <div className={styles.card}>
        <h1 className={styles.title}>{isEdit ? 'Edit User' : 'New User'}</h1>
        <p className={styles.subtitle}>
          {isEdit
            ? 'Update name, username, role, or set a new password.'
            : 'Create an account with access to the admin dashboard.'}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label}>Full Name *</label>
              <input
                className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                placeholder="e.g. Jane Smith"
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <p className={styles.fieldError}>{errors.name.message}</p>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Username *</label>
              <input
                className={`${styles.input} ${errors.username ? styles.inputError : ''}`}
                placeholder="e.g. janesmith"
                autoComplete="off"
                {...register('username', {
                  required: 'Username is required',
                  pattern: {
                    value: /^[a-zA-Z0-9_-]{3,30}$/,
                    message: 'Alphanumeric, underscore, hyphen — 3–30 characters',
                  },
                })}
              />
              {errors.username && <p className={styles.fieldError}>{errors.username.message}</p>}
            </div>

            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Role *</label>
              <select
                className={`${styles.input} ${errors.role ? styles.inputError : ''}`}
                {...register('role', { required: 'Role is required' })}
              >
                <option value="guest">Guest — limited access</option>
                <option value="admin">Admin — full access</option>
              </select>
              {errors.role && <p className={styles.fieldError}>{errors.role.message}</p>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                {isEdit ? 'New Password' : 'Password *'}
              </label>
              <div className={styles.inputWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`${styles.input} ${styles.inputPadded} ${errors.password ? styles.inputError : ''}`}
                  placeholder={isEdit ? 'Leave blank to keep current' : 'Min. 6 characters'}
                  autoComplete="new-password"
                  {...register('password', {
                    ...(isEdit ? {} : { required: 'Password is required' }),
                    minLength: { value: 6, message: 'At least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className={styles.fieldError}>{errors.password.message}</p>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                {isEdit ? 'Confirm New Password' : 'Confirm Password *'}
              </label>
              <div className={styles.inputWrap}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  className={`${styles.input} ${styles.inputPadded} ${errors.confirmPassword ? styles.inputError : ''}`}
                  placeholder="Repeat password"
                  autoComplete="new-password"
                  {...register('confirmPassword', {
                    ...(isEdit
                      ? { validate: (v) => !password || v === password || 'Passwords do not match' }
                      : {
                          required: 'Please confirm the password',
                          validate: (v) => v === password || 'Passwords do not match',
                        }),
                  })}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowConfirm((v) => !v)}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className={styles.fieldError}>{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            <button type="submit" className={styles.submitBtn} disabled={saving}>
              {saving ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </main>
  )
}
